import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";

const SearchBar = ({ cardData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Flatten all courses from all categories
  const allCourses = Object.values(cardData).flat();

  // Handle search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredResults([]);
      return;
    }

    const results = allCourses.filter((course) => {
      const searchLower = searchTerm.toLowerCase().trim();
      return (
        course.title.toLowerCase().includes(searchLower) ||
        course.subtitle.toLowerCase().includes(searchLower) ||
        course.level.toLowerCase().includes(searchLower) ||
        course.duration.toLowerCase().includes(searchLower) ||
        course.location.toLowerCase().includes(searchLower) ||
        course.cost.toLowerCase().includes(searchLower)
      );
    });

    setFilteredResults(results);
  }, [searchTerm, allCourses]);

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setSearchTerm("");
        setFilteredResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsSearchOpen(true);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredResults([]);
    setIsSearchOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle search item click
  const handleItemClick = () => {
    setSearchTerm("");
    setFilteredResults([]);
    setIsSearchOpen(false);
  };

  return (
    <div className="search-wrapper" ref={searchRef}>
      <div className="search-input-wrapper">
        
        <input
          ref={inputRef}
          type="text"
          className="form-control search-input"
          placeholder="Search courses by title, level, location..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setIsSearchOpen(true)}
        />
        {searchTerm && (
          <FaTimes 
            className="clear-icon" 
            onClick={clearSearch}
          />
        )}
      </div>

      {/* Search Results Dropdown */}
      {isSearchOpen && searchTerm && (
        <div className="search-results-dropdown">
          {filteredResults.length > 0 ? (
            <>
              <div className="search-results-header">
                <span>{filteredResults.length} courses found</span>
              </div>
              <div className="search-results-list">
                {filteredResults.map((course) => (
                  <Link
                    key={course.id}
                    to={course.link}
                    className="search-result-item"
                    onClick={handleItemClick}
                  >
                    <div className="result-image">
                      <img 
                        src={course.image} 
                        alt={course.title} 
                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "8px" }}
                      />
                    </div>
                    <div className="result-content">
                      <div className="result-title">{course.title}</div>
                      <div className="result-subtitle">{course.subtitle}</div>
                      <div className="result-meta">
                        <span className="result-level">{course.level}</span>
                        <span className="result-cost">{course.cost}</span>
                        <span className="result-duration">{course.duration}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              <FaSearch className="no-results-icon" />
              <p>No courses found for "{searchTerm}"</p>
              <small>Try searching with different keywords</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;